import { Link } from "react-router-dom";
import { Lock, ArrowRight } from "lucide-react";
import { Article } from "@/lib/types";
import { TxTitle, useArticlePath } from "@/lib/i18n";
import { homeTrack } from "../shared";

const FALLBACK = "https://images.unsplash.com/photo-1621761191319-c6fb62004040?crop=entropy&cs=srgb&fm=jpg&q=85&w=800";

const ACCENTS = [
  { num: "#A78BFA", cta: "text-[#A78BFA]", border: "hover:border-[#7C3AED]/55", glow: "hover:shadow-[0_12px_36px_rgba(124,58,237,0.18)]" },
  { num: "#22D3EE", cta: "text-[#22D3EE]", border: "hover:border-[#00E5FF]/45", glow: "hover:shadow-[0_12px_36px_rgba(34,211,238,0.14)]" },
  { num: "#A78BFA", cta: "text-[#A78BFA]", border: "hover:border-[#7C3AED]/55", glow: "hover:shadow-[0_12px_36px_rgba(124,58,237,0.18)]" },
];

export const PremiumBadge = () => (
  <span className="absolute top-3 left-3 z-10 inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 font-heading font-bold text-[9px] tracking-[0.16em] uppercase text-white bg-gradient-to-r from-[#7C3AED] to-[#8B5CF6] shadow-[0_2px_12px_rgba(124,58,237,0.45)]">
    <Lock className="h-3 w-3" aria-hidden /> Premium
  </span>
);

export const PremiumStoryCard = ({ a, index, isPremium }: { a: Article; index: number; isPremium: boolean }) => {
  const toArticle = useArticlePath();
  const t = ACCENTS[index % ACCENTS.length];
  const num = String(index + 1).padStart(2, "0");
  const dest = isPremium ? toArticle(a.slug) : `/premium?read=${a.slug}`;
  return (
    <Link to={dest} data-testid={`premium-story-${a.slug}`}
      onClick={() => homeTrack("premium_section", isPremium ? a.slug : "paywall_redirect")}
      className={`premium-card-stroke group relative flex flex-col rounded-xl border border-white/[0.08] bg-[#0b0c14] overflow-hidden motion-safe:transition-[transform,border-color,box-shadow] motion-safe:duration-200 hover:-translate-y-0.5 ${t.border} ${t.glow} focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#7C3AED]/70`}>
      <div className="relative aspect-[16/10] overflow-hidden bg-[#101116] shrink-0">
        <img src={a.hero_image_url || FALLBACK} alt={a.title} loading="lazy"
          onError={(e) => { (e.target as HTMLImageElement).src = FALLBACK; }}
          className="absolute inset-0 w-full h-full object-cover motion-safe:transition-transform motion-safe:duration-300 group-hover:scale-[1.02]" />
        <span aria-hidden className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-[#0b0c14] via-[#0b0c14]/55 to-transparent" />
        <PremiumBadge />
        <span aria-hidden className="absolute bottom-1.5 left-4 font-heading font-black text-[50px] leading-none select-none"
          style={{ color: t.num, opacity: 0.92, textShadow: `0 0 26px ${t.num}55, 0 2px 8px rgba(0,0,0,0.6)` }}>
          {num}
        </span>
      </div>
      <div className="flex flex-col flex-1 p-5">
        <h3 className="font-news font-bold text-white text-[16px] leading-snug line-clamp-2 group-hover:text-[#e6e0ff] transition-colors">
          <TxTitle slug={a.slug} title={a.title} />
        </h3>
        {(a.summary_ai || a.summary) && (
          <p className="text-[12.5px] text-gray-500 mt-2 line-clamp-3 leading-relaxed">{a.summary_ai || a.summary}</p>
        )}
        <div className="mt-auto pt-3.5 border-t border-white/[0.06] flex items-center justify-end">
          <span className={`inline-flex items-center gap-1 text-[12px] font-bold shrink-0 ${t.cta}`}>
            {isPremium ? "Read analysis" : "Unlock analysis"}
            <ArrowRight className="h-3 w-3 motion-safe:transition-transform motion-safe:duration-200 group-hover:translate-x-1" aria-hidden />
          </span>
        </div>
      </div>
    </Link>
  );
};
